import { Loader } from "@caprikorn/dynamo-ui";
import type { FC } from "react";

import { CenterContentLayout } from "@components/CenterContentLayout/CenterContentLayout";
import { RetryError } from "@components/errors/RetryError/RetryError";
import { Header } from "@components/Header/Header";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useGroupsDropdown } from "@hooks/useGroupsDropdown";
import { Folder } from "@icons";
import { ROUTES } from "@router/router.constants";
import { useGetDocumentsGroupSearch } from "@services/apiEcms/useGetDocumentsGroupSearch";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetPerimeterExport } from "@services/apiEcms/useGetPerimeterExport";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { DocumentsTable } from "./DocumentsTable/DocumentsTable";

export const Documents: FC = () => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation("main");
  const { groupsDropdownOptions, groupsOnDropdownChange, groupsDropdownValue } =
    useGroupsDropdown();

  const {
    data,
    isLoading: isGroupLoading,
    isError: isGroupDetailError,
  } = useGetGroup({
    groupId: currentGroup,
  });

  const {
    isError: isGetDocumentsError,
    isLoading: isDocumentsLoading,
    refetch: documentRefetch,
  } = useGetDocumentsGroupSearch();

  const {
    isError: isExtractError,
    isLoading: isExtractLoading,
    refetch: extractPerimRefetch,
  } = useGetPerimeterExport({ isAdmin: !!data?.isAdmin });

  const isError = isGetDocumentsError || isExtractError || isGroupDetailError;

  const isLoading = isExtractLoading || isDocumentsLoading || isGroupLoading;

  if (isLoading || isError) {
    return (
      <>
        <Header
          breadcrumbPaths={[
            {
              pathName: t("documents.breadcrumbTitle"),
              path: ROUTES.DOCUMENTS,
              pathIcon: Folder,
            },
          ]}
          dropdownOptions={groupsDropdownOptions}
          dropdownValue={groupsDropdownValue}
          onDropdownChange={(select) => groupsOnDropdownChange(select)}
        />
        <CenterContentLayout>
          {isError ? (
            <RetryError
              onRetry={() => {
                documentRefetch();
                extractPerimRefetch();
              }}
              text={t("documents.error.global")}
              title={t("documents.error.title")}
            />
          ) : (
            <Loader />
          )}
        </CenterContentLayout>
      </>
    );
  }

  return (
    <>
      <Header
        breadcrumbPaths={[
          {
            pathName: t("documents.breadcrumbTitle"),
            path: ROUTES.DOCUMENTS,
            pathIcon: Folder,
          },
        ]}
        dropdownOptions={groupsDropdownOptions}
        dropdownValue={groupsDropdownValue}
        onDropdownChange={groupsOnDropdownChange}
      />
      <DocumentsTable />
    </>
  );
};
